import { ConnectionTreeModel, EdgeModel, NodeModel } from 'api/connectionTree/connectionTree.model';

import { COLOR } from '../../styles/styledComponents/color.model';
import Node from './Node';

type ColorModel = {
  [key: string]: COLOR;
};

const getNodes = (certificateTree: ConnectionTreeModel): NodeModel[] => {
  return certificateTree.nodes.map((element, index) => {
    const xPositions = getXPositions(certificateTree);

    const backgroundColors: ColorModel = {
      default: COLOR.GREY_WHITE,
      other: COLOR.WHITE,
    };

    const borderColors: ColorModel = {
      default: COLOR.CHEMISTRY,
      other: COLOR.GUNSMOKE,
    };

    const nodeLevels = getNodeLevels(certificateTree);

    const nodePosition = {
      x: xPositions[index],
      y: nodeLevels[index] * (xPositions[index] !== 0 ? 150 : 200),
    };

    return {
      id: element.id,
      type: element.type,
      position: nodePosition,
      connectable: false,
      draggable: false,
      data: {
        label: (
          <Node
            name={element.data.name}
            origin={element.data.origin}
            recycleRate={element.data.recycleRate}
          />
        ),
        name: element.data.name,
        origin: element.data.origin,
        recycleRate: element.data.recycleRate,
      },
      style: {
        backgroundColor: [backgroundColors[element.type]],
        border: '1px solid',
        borderColor: [borderColors[element.type]],
        borderRadius: '5px',
        padding: '0px',
        width: '180px',
        height: '60px',
      },
    };
  });
};

const getEdges = (certificateTree: ConnectionTreeModel, nodes: NodeModel[]): EdgeModel[] => {
  return (
    certificateTree?.edges.map((element) => {
      const isDefault = element.target === nodes.find((element) => element.type === 'default')?.id;
      const edgeStyle = { stroke: [isDefault ? COLOR.CHEMISTRY : COLOR.IRON] };
      const labelStyle = {
        fontWeight: 'bold',
        fill: [isDefault ? COLOR.CHEMISTRY : [COLOR.GUNSMOKE]],
      };

      return {
        ...element,
        style: edgeStyle,
        labelStyle,
      };
    }) ?? []
  );
};

const getXPositions = (certificateTree: ConnectionTreeModel) => {
  const spacing = 300;
  const inputNodesCount = certificateTree
    ? certificateTree.nodes.filter(({ type }) => type === 'input').length
    : 0;
  const outputNodesCount = certificateTree
    ? certificateTree.nodes.filter(({ type }) => type === 'output').length
    : 0;
  const firstInputX = ((inputNodesCount - 1) * spacing) / -2;
  const firstOutputX = ((outputNodesCount - 1) * spacing) / -2;

  const inputNodes = Array(inputNodesCount)
    .fill(firstInputX)
    .reduce(
      (accumulator, currentXPos, index) => [...accumulator, currentXPos + index * spacing],
      []
    );
  const outputNodes = Array(outputNodesCount)
    .fill(firstOutputX)
    .reduce(
      (accumulator, currentXPos, index) => [...accumulator, currentXPos + index * spacing],
      []
    );

  const xPos: number[] = [0, ...inputNodes, ...outputNodes];

  return xPos;
};

const getNodeLevels = (certificateTree: ConnectionTreeModel) => {
  return (
    certificateTree?.nodes.map(({ type }) => {
      switch (type) {
        case 'input':
          return -1;
        case 'output':
          return 1;
        default:
          return 0;
      }
    }) ?? []
  );
};

export { getNodes, getEdges, getNodeLevels };
